import { useState, useEffect } from "react";
import axiosInstance from "../../axiosInstance";
import "./EaSettings.scss";
import { toast } from "react-toastify";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import { Icon } from "@iconify/react"; // Import Icon from @iconify/react
import GeneralTable from "../../components/Table/GeneralTable";
import { useAuth } from "../../contexts/AuthContext";
import IconButton from "../../components/IconButton/IconButton";

function EaSettings() {
  const { user, isLoading: isAuthLoading } = useAuth();

  const authorities = user.authorities || [];
  const isAdmin = authorities.includes("ROLE_ADMIN");

  const [emailConfig, setEmailConfig] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showModalPassword, setShowModalPassword] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingConfigId, setEditingConfigId] = useState(null);

  useEffect(() => {
    fetchEmailConfigs();
  }, [isAdmin]);

  const fetchEmailConfigs = async () => {
    try {
      const endpoint = isAdmin
        ? `${process.env.REACT_APP_API_BASE_URL}/email-config`
        : `${process.env.REACT_APP_API_HR_URL}/email-config`;

      const res = await axiosInstance.get(endpoint);

      const data = Array.isArray(res.data.data)
        ? res.data.data
        : [res.data.data];

      setEmailConfig(data);
      setFormData(data.length > 0 ? data[0] : {});

      if (isAdmin) {
        const empRes = await axiosInstance.get(
          `${process.env.REACT_APP_API_BASE_URL}/employees/email-config/All`,
        );

        console.log(empRes.data);

        setEmailConfig(empRes.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching email config:", err);
      setEmailConfig([]);
      setFormData({});
    }
  };

  const handleEdit = () => setIsEditing(true);

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(emailConfig.length > 0 ? emailConfig[0] : {});
    setShowPassword(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = () => {
    const url = isAdmin
      ? `${process.env.REACT_APP_API_BASE_URL}/email-config`
      : `${process.env.REACT_APP_API_HR_URL}/email-config`;

    const method = formData.emailConfigid ? "put" : "post";

    if (
      !formData.fromEmail ||
      !formData.smtpHost ||
      !formData.smtpPort ||
      !formData.username
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    axiosInstance[method](url, formData)
      .then(() => {
        toast.success(
          `Email configuration successfully ${
            method === "post" ? "created" : "updated"
          }`,
        );
        if (method === "post") {
          setEmailConfig((prevConfigs) => [...prevConfigs, formData]);
        } else {
          setEmailConfig((prevConfigs) =>
            prevConfigs.map((config) =>
              config.emailConfigid === formData.emailConfigid
                ? formData
                : config,
            ),
          );
        }
        setIsEditing(false);
      })
      .catch((err) => {
        toast.error(
          `Error ${method === "post" ? "creating" : "updating"} email config: ${
            err.message
          }`,
        );
      });
  };

  const handleEditConfig = async (config) => {
    try {
      console.log("Editing config:", config);
      console.log("Employee ID:", config.employeeId);

      if (!config.employeeId) {
        toast.error("Employee ID is missing.");
        return;
      }

      // Fetch the complete config using EMPLOYEE ID
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_BASE_URL}/employees/email-config/${config.employeeId}`,
      );

      const fullConfig = response.data.data || config;

      // Load employees for the dropdown
      const empRes = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/list`,
      );

      setEmployees(empRes.data.data || []);

      // Keep the employee ID from the selected row
      setFormData({
        ...fullConfig,
        employeeId: config.employeeId,
      });

      setSelectedEmployee(config.employeeId.toString());

      setIsEditMode(true);

      // Store employeeId because this is the ID used by the API
      setEditingConfigId(config.employeeId);

      setShowModal(true);
      setShowModalPassword(false);
    } catch (error) {
      console.error("Error fetching config details:", error);
      toast.error("Failed to load configuration details.");
    }
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this email config?")) {
      axiosInstance
        .delete(
          `${process.env.REACT_APP_API_BASE_URL}/employees/email-config/${id}`,
        )
        .then(() => {
          toast.success("Email config deleted successfully!");
          setEmailConfig((prevConfigs) =>
            prevConfigs.filter((config) => config.emailConfigid !== id),
          );
        })
        .catch((err) => {
          toast.error("Error deleting email config: " + err.message);
        });
    }
  };

  const handleCreateNewConfig = async () => {
    try {
      // Use the new API endpoint you provided
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/list`,
      );
      setEmployees(res.data.data || []);
      // Reset form with default values for checkboxes
      setFormData({
        smtpAuth: true,
        startTlsEnable: true,
        sslEnable: true,
        protocol: "smtp",
      });
      setSelectedEmployee("");
      setIsEditMode(false);
      setEditingConfigId(null);
      setShowModal(true);
      setShowModalPassword(false);
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employee list.");
    }
  };

  const handleModalSubmit = () => {
    // Make sure employeeId is included
    if (!selectedEmployee) {
      toast.error("Please select an employee");
      return;
    }

    if (
      !formData.fromEmail ||
      !formData.smtpHost ||
      !formData.smtpPort ||
      !formData.username ||
      (!isEditMode && !formData.password) // Password required only for new configs
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    const configData = {
      ...formData,
      employeeId: parseInt(selectedEmployee),
      smtpPort: parseInt(formData.smtpPort),
    };

    const method = isEditMode ? "put" : "post";
    const successMessage = isEditMode
      ? "Email configuration updated successfully!"
      : "Email configuration created successfully!";

    axiosInstance[method](
      `${process.env.REACT_APP_API_BASE_URL}/employees/email-config`,
      configData,
    )
      .then((response) => {
        toast.success(successMessage);
        setShowModal(false);
        setIsEditMode(false);
        setEditingConfigId(null);
        // Refresh the data
        fetchEmailConfigs();
      })
      .catch((err) => {
        toast.error(
          `Error ${isEditMode ? "updating" : "creating"} email config: ${
            err.message
          }`,
        );
      });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsEditMode(false);
    setEditingConfigId(null);
    setFormData({});
    setSelectedEmployee("");
  };

  const handleBackButton = () => {
    window.history.back();
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleModalPasswordVisibility = () => {
    setShowModalPassword(!showModalPassword);
  };

  // Define columns for GeneralTable - Password column removed
  const tableColumns = [
    {
      header: "Name",
      accessor: "fullName",
    },
    {
      header: "From Email",
      accessor: "fromEmail",
    },
    {
      header: "SMTP Host",
      accessor: "smtpHost",
    },
    {
      header: "SMTP Port",
      accessor: "smtpPort",
    },
    {
      header: "Username",
      accessor: "username",
    },
    {
      header: "Protocol",
      accessor: "protocol",
    },
    {
      header: "Actions",
      accessor: "actions",
    },
  ];

  // Transform data to include action buttons (password column removed)
  const transformedTableData = emailConfig.map((config) => ({
    ...config,
    actions: (
      <div className="action-container flex gap-2">
        <IconButton
          icon="mdi:pencil-outline"
          variant={"edit"}
          onClick={() => handleEditConfig(config)}
        />
        <IconButton
          icon="mdi:delete"
          variant={"delete"}
          onClick={() => handleDelete(config.emailConfigid)}
        />
      </div>
    ),
  }));

  return (
    <div className="ea-settings">
      <h1 className="display6">Email Configuration</h1>

      <button type="button" className="back-button" onClick={handleBackButton}>
        ← Back
      </button>

      <div className="content-container">
        {!isAdmin && (
          <div className="email-card">
            <div className="card-header">
              <h3>Email Configuration</h3>
              <div>
                {!isEditing ? (
                  <ButtonWithIcon
                    icon="mdi:pencil-outline"
                    text="Edit"
                    onClick={handleEdit}
                    className="ea-edit-button"
                  />
                ) : (
                  <div className="action-container">
                    <ButtonWithIcon
                      icon="mdi:floppy-disk"
                      text=""
                      onClick={handleSave}
                      className="save-button"
                    />
                    <ButtonWithIcon
                      icon="mdi:close"
                      text=""
                      onClick={handleCancel}
                      className="cancel-button"
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="card-body">
              {!isEditing ? (
                emailConfig.length > 0 ? (
                  <>
                    <p>
                      <strong>From Email:</strong>{" "}
                      {emailConfig[0].fromEmail || "N/A"}
                    </p>
                    <p>
                      <strong>SMTP Host:</strong>{" "}
                      {emailConfig[0].smtpHost || "N/A"}
                    </p>
                    <p>
                      <strong>SMTP Port:</strong>{" "}
                      {emailConfig[0].smtpPort || "N/A"}
                    </p>
                    <p>
                      <strong>Username:</strong>{" "}
                      {emailConfig[0].username || "N/A"}
                    </p>
                    <p>
                      <strong>Password:</strong>{" "}
                      {emailConfig[0].password ? "••••••••" : "N/A"}
                    </p>
                    <p>
                      <strong>Protocol:</strong>{" "}
                      {emailConfig[0].protocol || "N/A"}
                    </p>
                  </>
                ) : (
                  <p>No email configuration found.</p>
                )
              ) : (
                <>
                  <label>From Email:</label>
                  <input
                    type="email"
                    name="fromEmail"
                    value={formData.fromEmail || ""}
                    onChange={handleChange}
                  />
                  <label>SMTP Host:</label>
                  <input
                    type="text"
                    name="smtpHost"
                    value={formData.smtpHost || ""}
                    onChange={handleChange}
                  />
                  <label>SMTP Port:</label>
                  <input
                    type="number"
                    name="smtpPort"
                    value={formData.smtpPort || ""}
                    onChange={handleChange}
                  />
                  <label>Username:</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username || ""}
                    onChange={handleChange}
                  />
                  <label>Password:</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                    />
                    <span
                      className="password-toggle"
                      onClick={togglePasswordVisibility}
                    >
                      <Icon icon={showPassword ? "mdi:eye-off" : "mdi:eye"} />
                    </span>
                  </div>
                  <label>Protocol:</label>
                  <input
                    type="text"
                    name="protocol"
                    value={formData.protocol || ""}
                    onChange={handleChange}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {isAdmin && (
          <div className="email-config-table">
            <div className="table-actions">
              <ButtonWithIcon
                icon="mdi:plus"
                text="Create Config"
                onClick={handleCreateNewConfig}
                className="create-button"
              />
            </div>

            <GeneralTable
              columns={tableColumns}
              data={transformedTableData}
              title="Email Configurations"
            />
          </div>
        )}

        {showModal && (
          <div className="ea-overlay">
            <div className="ea-content">
              <h3 className="ea-title">
                {isEditMode
                  ? "Edit Email Configuration"
                  : "Create New Email Configuration"}
              </h3>

              <div className="ea-form-grid">
                <div className="ea-form-group">
                  <label>Select Employee:</label>
                  <select
                    value={selectedEmployee}
                    onChange={(e) => setSelectedEmployee(e.target.value)}
                    disabled={isEditMode} // Disable employee selection in edit mode
                  >
                    <option value="">-- Select Employee --</option>
                    {employees.map((emp) => (
                      <option key={emp.empId} value={emp.empId}>
                        {emp.firstName} {emp.lastName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ea-form-group">
                  <label>From Email:</label>
                  <input
                    type="email"
                    name="fromEmail"
                    value={formData.fromEmail || ""}
                    onChange={handleChange}
                    placeholder="example@company.com"
                  />
                </div>

                <div className="ea-form-group">
                  <label>SMTP Host:</label>
                  <input
                    type="text"
                    name="smtpHost"
                    value={formData.smtpHost || ""}
                    onChange={handleChange}
                    placeholder="smtp.example.com"
                  />
                </div>

                <div className="ea-form-group">
                  <label>SMTP Port:</label>
                  <input
                    type="number"
                    name="smtpPort"
                    value={formData.smtpPort || ""}
                    onChange={handleChange}
                    placeholder="587"
                  />
                </div>

                <div className="ea-form-group">
                  <label>Username:</label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username || ""}
                    onChange={handleChange}
                    placeholder="username@example.com"
                  />
                </div>

                <div className="ea-form-group">
                  <label>
                    Password:{" "}
                    {isEditMode && (
                      <small>(Leave blank to keep current password)</small>
                    )}
                  </label>
                  <div className="password-input-wrapper">
                    <input
                      type={showModalPassword ? "text" : "password"}
                      name="password"
                      value={formData.password || ""}
                      onChange={handleChange}
                      placeholder={
                        isEditMode
                          ? "Enter new password or leave blank"
                          : "••••••••"
                      }
                    />
                    <span
                      className="password-toggle"
                      onClick={toggleModalPasswordVisibility}
                    >
                      <Icon
                        icon={showModalPassword ? "mdi:eye-off" : "mdi:eye"}
                      />
                    </span>
                  </div>
                </div>

                <div className="ea-form-group">
                  <label>Protocol:</label>
                  <input
                    type="text"
                    name="protocol"
                    value={formData.protocol || ""}
                    onChange={handleChange}
                    placeholder="smtp"
                  />
                </div>

                <div className="ea-form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="smtpAuth"
                      checked={formData.smtpAuth || false}
                      onChange={handleChange}
                    />
                    SMTP Authentication
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="startTlsEnable"
                      checked={formData.startTlsEnable || false}
                      onChange={handleChange}
                    />
                    Enable STARTTLS
                  </label>

                  <label>
                    <input
                      type="checkbox"
                      name="sslEnable"
                      checked={formData.sslEnable || false}
                      onChange={handleChange}
                    />
                    Enable SSL
                  </label>
                </div>
              </div>

              <div className="modal-actions">
                <ButtonWithIcon
                  icon="mdi:floppy-disk"
                  text={isEditMode ? "Update" : "Save"}
                  onClick={handleModalSubmit}
                  className="save-button"
                />
                <ButtonWithIcon
                  icon="mdi:close"
                  text="Cancel"
                  onClick={handleCloseModal}
                  className="cancel-button"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default EaSettings;
